import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { APP_URL_CHAT, CHAT_TYPE, STORAGE_KEY_PENDING_ADVISOR_ALIAS, STORAGE_KEY_SELF_CREATED_CHAT } from '../../../const';
import { useZaraTrial } from '../../../hooks/useZaraTrial';
import storageService from '../../../service/StorageService';
import {
    ADVISOR_LOCK_REASON,
    ZARA_ADVISOR_ALIAS,
    getAdvisorLockReason,
    isPremiumSubscriber,
    shouldShowZaraWelcome
} from '../../../service/advisorAccess';
import { getSeenAdvisorIntros, getUser, toggleAuthModal } from '../../../store/slices/authSlice';
import {
    setMultyChatInvationModalVisibility,
    startChatWithAdvisor,
    startChatWithAdvisorByActivities
} from '../../../store/slices/chatsSlice';
import { openPaymentModal, setShowZaraUpsellModal, setShowZaraWelcomeModal } from '../../../store/slices/modalsSlice';
import styles from './styles.module.scss';

const MarketPlaceActionButton = ({
    isToCatalog = false,
    text,
    advisorId = '',
    advisorAlias = '',
    classes = [],
    chatType = CHAT_TYPE.SINGLE,
    isTransparent = false,
    isActivities = false
}) => {
    const user = useSelector(getUser);
    const seenAdvisorIntros = useSelector(getSeenAdvisorIntros);
    const { isExhausted: zaraTrialExhausted } = useZaraTrial();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [openChatLoading, setOpenChatLoading] = useState(false);
    const { t } = useTranslation();

    const handleChat = useCallback(async () => {
        // Simple single-chat path (the "Chat now" button on /catalog and
        // the landing-page advisor cards): just navigate to the advisor's
        // landing URL. The chat is created lazily when the user actually
        // engages on that page — taps a home card or sends a message —
        // not proactively on this button tap. Avoids producing orphan
        // chats from people who tap "Chat now" and then leave the site.
        //
        // The other two paths still need the chat created up-front:
        //   - Activity-based chats (`isActivities`) are bound to an
        //     activityId, and only the chat-creation response carries
        //     the advisor alias to navigate to.
        //   - Multichat (`CHAT_TYPE.MULTIPLY`) opens an invitation modal
        //     immediately afterwards that consumes the created chat's id.
        if (chatType === CHAT_TYPE.SINGLE && !isActivities && advisorAlias) {
            navigate(`/${APP_URL_CHAT}/${advisorAlias}`);
            return;
        }

        setOpenChatLoading(true);
        const routStringPath = advisorId ? advisorId : `alias/${advisorAlias}`;
        const result = isActivities
            ? await dispatch(startChatWithAdvisorByActivities(advisorId))
            : await dispatch(startChatWithAdvisor(routStringPath));

        if (result?.meta?.requestStatus === 'fulfilled') {
            const responseAlias = result?.payload?.advisor?.alias;
            const advisor = responseAlias || advisorAlias;
            storageService.add(STORAGE_KEY_SELF_CREATED_CHAT, result?.payload?.id);
            navigate(`/${APP_URL_CHAT}/${advisor}/${result?.payload?.id}`);
            if (chatType === CHAT_TYPE.MULTIPLY) {
                dispatch(setMultyChatInvationModalVisibility(true));
            }
        }
        setOpenChatLoading(false);
    }, [advisorAlias, advisorId, dispatch, navigate, isActivities, chatType]);

    const openChat = useCallback(() => {
        if (openChatLoading) return;

        if (chatType === CHAT_TYPE.MULTIPLY && !user?.id) {
            dispatch(toggleAuthModal({ trigger: true, step: 1 }));
            return;
        }
        // HORO-10504: catalog lock gate (anonymous character-access, plus
        // Zara's premium-subscriber gate) — MULTIPLY is already covered by
        // the login requirement just above for every non-Zara advisor, so
        // this only matters for the SINGLE/advisor-specific path and Zara.
        const lockReason = getAdvisorLockReason(advisorAlias, {
            isUser: !!user?.id,
            isPremiumUser: isPremiumSubscriber(user),
            zaraTrialExhausted
        });
        if (lockReason === ADVISOR_LOCK_REASON.REGISTER) {
            // HORO-10599: Zara's REGISTER case (anon viewer — she's the
            // register-to-chat funnel) opens her own modal instead of the
            // generic register flow, same special-casing the SUBSCRIBE
            // branch below already does. ZaraUpsellModal itself stashes the
            // pending alias and opens the auth modal once its own CTA is
            // tapped.
            if (advisorAlias === ZARA_ADVISOR_ALIAS) {
                dispatch(setShowZaraUpsellModal(true));
                return;
            }
            if (typeof sessionStorage !== 'undefined') {
                sessionStorage.setItem(STORAGE_KEY_PENDING_ADVISOR_ALIAS, advisorAlias);
            }
            dispatch(toggleAuthModal({ trigger: true, step: 1, context: 'advisorLocked' }));
            return;
        }
        if (lockReason === ADVISOR_LOCK_REASON.SUBSCRIBE) {
            // HORO-10504: opens the Zara upsell modal (ZaraUpsellModal,
            // mounted in App.js) instead of falling through to handleChat,
            // which would otherwise navigate into a chat the backend just
            // rejects for a non-subscriber.
            //
            // HORO-10562: every other advisor beyond the registered-tier
            // roster reports this same reason now — for those, open the
            // app's already-generic subscribe flow (PaymentModal) instead
            // of Zara's bespoke one, tagging the origin so
            // PaymentSuccessPage's "Back to Chat" returns here.
            //
            // HORO-10563: PaymentModal itself now branches on whether the
            // viewer is signed in (an anonymous tap gets the same
            // "Ask without limits" paywall, registering first since
            // /checkout is a protected route) — this dispatch no longer
            // needs to know or care which.
            if (advisorAlias === ZARA_ADVISOR_ALIAS) {
                dispatch(setShowZaraUpsellModal(true));
            } else {
                dispatch(openPaymentModal({ source: 'advisorLocked', advisorAlias: advisorAlias }));
            }
            return;
        }
        // HORO-10504: unlocked (premium) Zara, but this account has never
        // been through her one-time welcome screen — show that instead of
        // going straight into a chat.
        if (shouldShowZaraWelcome(advisorAlias, { isPremiumUser: isPremiumSubscriber(user), seenAdvisorIntros })) {
            dispatch(setShowZaraWelcomeModal(true));
            return;
        }
        handleChat();
    }, [advisorAlias, chatType, dispatch, handleChat, openChatLoading, seenAdvisorIntros, user, zaraTrialExhausted]);

    useEffect(() => {
        setOpenChatLoading(false);
    }, []);

    return isToCatalog ? (
        <Link to={'/catalog'} className={classNames(styles.btn, ...classes)}>
            {text || t('buttons.chatNow')}
        </Link>
    ) : (
        <button
            className={classNames(styles.btn, { [styles.transparent]: isTransparent }, ...classes, {
                [styles.noEvents]: openChatLoading
            })}
            onClick={openChat}
        >
            {openChatLoading ? (
                <span className={styles.loading}>Loading</span>
            ) : chatType === CHAT_TYPE.SINGLE ? (
                text || t('buttons.chatNow')
            ) : (
                text || t('buttons.multiChat')
            )}
        </button>
    );
};

export default MarketPlaceActionButton;
